import React, { useEffect, useMemo, useState, useDeferredValue, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, Sparkles, ArrowUpRight } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { StockCategoryFilter, CATEGORY_CONFIGS } from '../../../types/stockCategories';
import {
  normalizeStockOption,
  NormalizedStockOption
} from '../../../utils/stockOptionFormatting';

export interface StockComboboxProps {
  options: string[];
  onSelect: (option: string) => void;
  /** Called when Enter is pressed to select - allows parent to also submit */
  onSubmit?: (option: string) => void;
  category?: StockCategoryFilter;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  selectAllTrigger?: number;
}

const MAX_RENDERED_RESULTS = 100;
const LISTBOX_ID = 'stock-combobox-listbox';

// Helper functions for highlighting
const renderTickerHighlight = (option: NormalizedStockOption): React.ReactNode => {
  if (!option.ticker) {
    return null;
  }

  if (option.matchHighlight?.field === 'ticker') {
    const highlightLength = Math.min(option.matchHighlight.length, option.ticker.length);
    return (
      <>
        <span className="font-semibold">{option.ticker.slice(0, highlightLength)}</span>
        {option.ticker.slice(highlightLength)}
      </>
    );
  }

  return option.ticker;
};

const renderCompanyHighlight = (option: NormalizedStockOption): React.ReactNode => {
  const { company, matchHighlight } = option;

  if (!matchHighlight || matchHighlight.field === 'ticker') {
    return company;
  }

  if (matchHighlight.field === 'company') {
    const highlightLength = Math.min(matchHighlight.length, company.length);
    return (
      <>
        <span className="font-semibold">{company.slice(0, highlightLength)}</span>
        {company.slice(highlightLength)}
      </>
    );
  }

  const words = company.split(/[\s-]+/);
  const separators = company.match(/[\s-]+/g) ?? [];
  const targetIndex = Math.max(0, Math.min(matchHighlight.wordIndex ?? 0, words.length - 1));
  const highlightLength = Math.min(matchHighlight.length, words[targetIndex]?.length ?? 0);
  const nodes: React.ReactNode[] = [];

  words.forEach((word, index) => {
    if (index > 0) {
      nodes.push(
        <React.Fragment key={`sep-${index}`}>
          {separators[index - 1] ?? ' '}
        </React.Fragment>
      );
    }

    if (!word) {
      nodes.push(<React.Fragment key={`word-${index}`}>{word}</React.Fragment>);
      return;
    }

    if (index === targetIndex && highlightLength > 0) {
      nodes.push(
        <React.Fragment key={`word-${index}`}>
          <span className="font-semibold">{word.slice(0, highlightLength)}</span>
          {word.slice(highlightLength)}
        </React.Fragment>
      );
    } else {
      nodes.push(<React.Fragment key={`word-${index}`}>{word}</React.Fragment>);
    }
  });

  return nodes;
};

// Memoized option row to prevent re-renders when highlightedIndex changes
interface OptionRowProps {
  option: NormalizedStockOption;
  index: number;
  isHighlighted: boolean;
  onSelect: (value: string) => void;
  onMouseEnter: (index: number) => void;
}

const OptionRow = React.memo<OptionRowProps>(({
  option,
  index,
  isHighlighted,
  onSelect,
  onMouseEnter
}) => {
  return (
    <li
      id={`stock-option-${index}`}
      role="option"
      aria-selected={isHighlighted}
    >
      <button
        type="button"
        onClick={() => onSelect(option.value)}
        onMouseEnter={() => onMouseEnter(index)}
        className={cn(
          'group flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
          'focus-visible:outline-none',
          isHighlighted
            ? 'bg-primary/10 dark:bg-primary/20'
            : 'hover:bg-muted/50'
        )}
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {option.ticker && (
              <span
                className={cn(
                  'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                  isHighlighted
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-primary/15 text-primary'
                )}
              >
                {renderTickerHighlight(option)}
              </span>
            )}
            <span className="text-sm font-medium text-foreground truncate">
              {renderCompanyHighlight(option)}
            </span>
          </div>
        </div>

        <span
          className={cn(
            'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-colors',
            isHighlighted
              ? 'bg-primary/15 text-primary'
              : 'text-muted-foreground group-hover:text-primary'
          )}
        >
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </button>
    </li>
  );
});

OptionRow.displayName = 'OptionRow';

/**
 * Floating modal panel for stock search - Spotlight/command-palette style.
 * Rendered via portal, centered in viewport with backdrop overlay.
 */
interface FloatingPanelCloseOptions {
  refocus?: boolean;
}

interface FloatingPanelProps {
  openedAtRef: React.MutableRefObject<number>;
  isOpen: boolean;
  searchTerm: string;
  filteredOptions: NormalizedStockOption[];
  highlightedIndex: number;
  onSelect: (value: string) => void;
  onClose: (options?: FloatingPanelCloseOptions) => void;
  onHighlightChange: (index: number) => void;
  categoryMeta: { label: string } | null;
  optionsCount: number;
  listRef: React.MutableRefObject<HTMLUListElement | null>;
  panelRef: React.MutableRefObject<HTMLDivElement | null>;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  placeholder: string;
  disabled: boolean;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onClearInput: () => void;
}

const FloatingPanel: React.FC<FloatingPanelProps> = ({
  openedAtRef,
  isOpen,
  searchTerm,
  filteredOptions,
  highlightedIndex,
  onSelect,
  onClose,
  onHighlightChange,
  categoryMeta,
  optionsCount,
  listRef,
  panelRef,
  inputRef,
  placeholder,
  disabled,
  onInputChange,
  onInputKeyDown,
  onClearInput
}) => {
  // Click outside detection
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (Date.now() - openedAtRef.current < 300) return;
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose({ refocus: true });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose, panelRef]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose({ refocus: true });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus search input when panel opens
  useEffect(() => {
    if (!isOpen) return;
    const inputEl = inputRef.current;
    if (!inputEl) return;

    const focusInput = () => {
      inputEl.focus({ preventScroll: true });
      const length = inputEl.value.length;
      requestAnimationFrame(() => {
        inputEl.setSelectionRange(length, length);
      });
    };

    const frame = requestAnimationFrame(focusInput);
    return () => cancelAnimationFrame(frame);
  }, [isOpen, inputRef]);

  if (!isOpen) return null;

  const hasQuery = searchTerm.trim().length > 0;
  const hasResults = filteredOptions.length > 0;
  const isResultTrimmed = filteredOptions.length > MAX_RENDERED_RESULTS;
  const visibleOptions = isResultTrimmed
    ? filteredOptions.slice(0, MAX_RENDERED_RESULTS)
    : filteredOptions;
  const showEmptyState = !hasQuery && !hasResults;
  const showNoResults = hasQuery && !hasResults;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[12vh]">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-transparent z-10"
        onMouseDown={() => {
          if (Date.now() - openedAtRef.current < 300) return;
          onClose({ refocus: true });
        }}
      />
      <div
        ref={panelRef}
        className={cn(
          "relative z-20 w-full mx-4 max-w-[560px]",
          "bg-card border border-border/70 rounded-2xl shadow-2xl",
          "flex flex-col max-h-[70vh] overflow-hidden"
        )}
        style={{
          boxShadow: '0 30px 60px -15px rgba(0, 0, 0, 0.4), 0 15px 30px -10px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08)'
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Stock search"
      >
        {/* Input + helper text */}
        <div className="flex-shrink-0 px-4 py-4 border-b border-border/70 bg-background/70 backdrop-blur-sm space-y-3 relative">
          <button
            type="button"
            onClick={() => onClose({ refocus: true })}
            className="absolute top-2 right-2 z-20 inline-flex h-8 w-8 items-center justify-center rounded-full border border-transparent bg-red-600 text-white shadow hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            aria-label="Close stock selector"
          >
            X
          </button>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
              <Search className="h-4 w-4" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={onInputChange}
              onKeyDown={onInputKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              aria-expanded={true}
              aria-haspopup="dialog"
              aria-controls={LISTBOX_ID}
              aria-autocomplete="list"
              aria-activedescendant={
                highlightedIndex >= 0 ? `stock-option-${highlightedIndex}` : undefined
              }
              role="combobox"
              className={cn(
                "w-full rounded-xl border border-border pl-10 pr-16 py-3 text-base",
                "bg-[#E3F0FF] text-slate-900 placeholder:text-slate-500",
                "dark:bg-[#F4F0E4] dark:text-slate-900 dark:placeholder:text-slate-600",
                "focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary",
                "disabled:opacity-60 disabled:cursor-not-allowed",
                "transition-colors"
              )}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={onClearInput}
                className="absolute inset-y-1 right-1 rounded-lg px-3 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                Clear
              </button>
            )}
          </div>
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            <span>
              Type <span className="rounded bg-muted px-1.5 py-0.5 font-semibold text-foreground">$</span> for ticker search
              {categoryMeta && (
                <span className="ml-1.5">
                  • {optionsCount.toLocaleString()} {categoryMeta.label} stocks
                </span>
              )}
            </span>
          </p>
        </div>

        {/* Results area */}
        <div className="flex-1 overflow-y-auto p-2 min-h-[200px] bg-card/80">
          {showEmptyState ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <Search className="h-10 w-10 text-muted-foreground/40 mb-4" />
              <p className="text-sm font-medium text-muted-foreground">
                Start typing to search stocks...
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Search by company name or $TICKER
              </p>
            </div>
          ) : showNoResults ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <p className="text-sm font-medium text-muted-foreground">
                No matches found for "{searchTerm}"
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Try another name or remove the $ prefix
              </p>
            </div>
          ) : (
            <ul
              ref={listRef}
              id={LISTBOX_ID}
              role="listbox"
              className="space-y-0.5"
            >
              {visibleOptions.map((option, index) => (
                <OptionRow
                  key={option.original}
                  option={option}
                  index={index}
                  isHighlighted={highlightedIndex === index}
                  onSelect={onSelect}
                  onMouseEnter={onHighlightChange}
                />
              ))}
            </ul>
          )}
          {isResultTrimmed && hasResults && (
            <div className="px-3 py-2 text-center text-[11px] text-muted-foreground border-t border-border/50 mt-2">
              Showing first {MAX_RENDERED_RESULTS.toLocaleString()} matches
            </div>
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="flex-shrink-0 px-4 py-2 border-t border-border/70 bg-background/70">
          <p className="text-[10px] text-muted-foreground text-center">
            <kbd className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">↑↓</kbd>
            <span className="mx-1.5">navigate</span>
            <span className="mx-1">•</span>
            <kbd className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">Enter</kbd>
            <span className="mx-1.5">select</span>
            <span className="mx-1">•</span>
            <kbd className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd>
            <span className="mx-1.5">close</span>
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * StockCombobox - A Spotlight/command-palette style stock search.
 * Opens floating modal panel on focus, filters as you type.
 */
export const StockCombobox: React.FC<StockComboboxProps> = ({
  options,
  onSelect,
  onSubmit,
  category,
  placeholder = 'Search by company or ticker (e.g. Apple / $AAPL)',
  disabled = false,
  className,
  selectAllTrigger = 0
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasTypedOnce, setHasTypedOnce] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const triggerInputRef = useRef<HTMLInputElement>(null);
  const panelInputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const ignoreNextFocusRef = useRef(false);
  const openedAtRef = useRef(0);

  // Track when panel opens for timestamp guards on close mechanisms
  useEffect(() => {
    if (isOpen) {
      openedAtRef.current = Date.now();
    }
  }, [isOpen]);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Normalize and sort options
  const normalizedOptions = useMemo<NormalizedStockOption[]>(
    () => {
      const normalized = options.map((option) => normalizeStockOption(option));
      return normalized.sort((a, b) =>
        a.company.localeCompare(b.company, undefined, { sensitivity: 'base' })
      );
    },
    [options]
  );

  // Filter options based on search term - only when query is non-empty
  const filteredOptions = useMemo(() => {
    const trimmedSearch = deferredSearchTerm.trim();

    // When query is empty, only show options if the user has typed before
    if (!trimmedSearch) {
      return hasTypedOnce ? normalizedOptions : [];
    }

    const isTickerQuery = trimmedSearch.startsWith('$');
    const normalizedSearchTerm = isTickerQuery
      ? trimmedSearch.slice(1).toLowerCase()
      : trimmedSearch.toLowerCase();

    const applyHighlight = (
      option: NormalizedStockOption,
      field: 'ticker' | 'company' | 'word-start',
      length: number,
      wordIndex?: number
    ): NormalizedStockOption => ({
      ...option,
      matchHighlight: { field, length, wordIndex }
    });

    // Tier 1: Company name matches
    const companyMatchesRaw = !isTickerQuery
      ? normalizedOptions.filter((option) =>
          option.company.toLowerCase().startsWith(normalizedSearchTerm)
        )
      : [];

    const companyMatches = companyMatchesRaw
      .sort((a, b) => a.company.localeCompare(b.company, undefined, { sensitivity: 'base' }))
      .map((option) => applyHighlight(option, 'company', normalizedSearchTerm.length));

    const seenValues = new Set(companyMatchesRaw.map((option) => option.value));

    // Tier 2: Ticker matches
    const tickerMatchesRaw = normalizedOptions.filter((option) => {
      if (!option.ticker) return false;
      if (!isTickerQuery && seenValues.has(option.value)) return false;
      return option.ticker.toLowerCase().startsWith(normalizedSearchTerm);
    });

    const tickerMatches = tickerMatchesRaw
      .sort((a, b) => (a.ticker || '').localeCompare(b.ticker || '', undefined, { sensitivity: 'base' }))
      .map((option) => applyHighlight(option, 'ticker', normalizedSearchTerm.length));

    tickerMatchesRaw.forEach((option) => seenValues.add(option.value));

    // Tier 3: Word-start matches
    const wordStartMatchesRaw = !isTickerQuery
      ? normalizedOptions.filter((option) => {
          if (seenValues.has(option.value)) return false;
          const words = option.company.split(/[\s-]+/);
          return words.some((word, index) => {
            if (index === 0) return false;
            return word.toLowerCase().startsWith(normalizedSearchTerm);
          });
        })
      : [];

    const wordStartMatches = wordStartMatchesRaw
      .sort((a, b) => a.company.localeCompare(b.company, undefined, { sensitivity: 'base' }))
      .map((option) => {
        const words = option.company.split(/[\s-]+/);
        const matchedWordIndex = words.findIndex((word, index) =>
          index > 0 && word.toLowerCase().startsWith(normalizedSearchTerm)
        );
        return applyHighlight(
          option,
          'word-start',
          normalizedSearchTerm.length,
          matchedWordIndex >= 0 ? matchedWordIndex : undefined
        );
      });

    return isTickerQuery
      ? tickerMatches
      : [...companyMatches, ...tickerMatches, ...wordStartMatches];
  }, [normalizedOptions, deferredSearchTerm, hasTypedOnce]);

  const visibleOptions = filteredOptions.length > MAX_RENDERED_RESULTS
    ? filteredOptions.slice(0, MAX_RENDERED_RESULTS)
    : filteredOptions;

  // Scroll highlighted item into view (no smooth scrolling)
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Stable callback for selection
  const handleSelect = useCallback((value: string) => {
    onSelect(value);
    onSubmit?.(value);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    // Re-focus trigger but suppress the focus handler from reopening the panel
    ignoreNextFocusRef.current = true;
    triggerInputRef.current?.focus();
    requestAnimationFrame(() => {
      ignoreNextFocusRef.current = false;
    });
  }, [onSelect, onSubmit]);

  // Handle closing the panel
  const handleClose = useCallback((options?: FloatingPanelCloseOptions) => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (options?.refocus) {
      ignoreNextFocusRef.current = true;
      triggerInputRef.current?.focus();
      requestAnimationFrame(() => {
        ignoreNextFocusRef.current = false;
      });
    }
  }, [triggerInputRef]);

  // Close overlay if focus moves elsewhere (e.g. user tabs away)
  useEffect(() => {
    if (!isOpen) return;

    const handleFocusIn = (event: FocusEvent) => {
      if (Date.now() - openedAtRef.current < 300) return;
      const target = event.target as Node;
      if (triggerInputRef.current && triggerInputRef.current.contains(target)) {
        return;
      }
      if (panelRef.current && panelRef.current.contains(target)) {
        return;
      }
      handleClose();
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [isOpen, handleClose, triggerInputRef, panelRef]);

  // Handle highlight change from mouse hover
  const handleHighlightChange = useCallback((index: number) => {
    setHighlightedIndex(index);
  }, []);

  // Open the palette when parent requests "select all"
  useEffect(() => {
    if (!selectAllTrigger) return;
    setHasTypedOnce(true);
    setIsOpen(true);
    requestAnimationFrame(() => {
      panelInputRef.current?.focus({ preventScroll: true });
    });
  }, [selectAllTrigger]);

  // Clear search while keeping focus inside panel
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    panelInputRef.current?.focus();
  }, [panelInputRef]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      // Open on any navigation key when closed
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setIsOpen(true);
        if (visibleOptions.length > 0) {
          setHighlightedIndex(e.key === 'ArrowDown' ? 0 : visibleOptions.length - 1);
        }
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (visibleOptions.length > 0) {
          setHighlightedIndex(prev =>
            prev < visibleOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (visibleOptions.length > 0) {
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (visibleOptions.length > 0) {
          // Select highlighted item, or first item if none highlighted
          const indexToSelect = highlightedIndex >= 0 ? highlightedIndex : 0;
          handleSelect(visibleOptions[indexToSelect].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        handleClose({ refocus: true });
        break;
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!hasTypedOnce && value.trim().length > 0) {
      setHasTypedOnce(true);
    }
    // Panel should already be open from focus, but ensure it stays open
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle input focus - opens the floating panel
  const handleFocus = () => {
    if (!disabled) {
      if (ignoreNextFocusRef.current) {
        ignoreNextFocusRef.current = false;
        return;
      }
      setIsOpen(true);
    }
  };

  const categoryMeta = category ? CATEGORY_CONFIGS[category] : null;

  return (
    <div className={cn("relative w-full", className)}>
      {/* Input Field (hidden when modal open) */}
      <div
        className={cn(
          "relative transition-opacity duration-150",
          isOpen && "pointer-events-none opacity-0"
        )}
        aria-hidden={isOpen}
      >
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
          <Search className="h-4 w-4" />
        </div>
        <input
          ref={triggerInputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-controls={LISTBOX_ID}
          aria-autocomplete="list"
          aria-activedescendant={
            highlightedIndex >= 0 ? `stock-option-${highlightedIndex}` : undefined
          }
          role="combobox"
          className={cn(
            "w-full rounded-xl border border-border pl-10 pr-4 py-2.5 text-sm",
            "bg-[#E3F0FF] text-slate-900 placeholder:text-slate-500",
            "dark:bg-[#F4F0E4] dark:text-slate-900 dark:placeholder:text-slate-600",
            "focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary",
            "disabled:opacity-60 disabled:cursor-not-allowed",
            "transition-colors"
          )}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              triggerInputRef.current?.focus();
            }}
            className="absolute inset-y-1 right-1 rounded-lg px-3 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Clear
          </button>
        )}
      </div>

      {/* Floating Modal Panel (rendered via portal) */}
      <FloatingPanel
        openedAtRef={openedAtRef}
        isOpen={isOpen}
        searchTerm={searchTerm}
        filteredOptions={filteredOptions}
        highlightedIndex={highlightedIndex}
        onSelect={handleSelect}
        onClose={handleClose}
        onHighlightChange={handleHighlightChange}
        categoryMeta={categoryMeta}
        optionsCount={options.length}
        listRef={listRef}
        panelRef={panelRef}
        inputRef={panelInputRef}
        placeholder={placeholder}
        disabled={disabled}
        onInputChange={handleInputChange}
        onInputKeyDown={handleKeyDown}
        onClearInput={handleClearSearch}
      />
    </div>
  );
};

// Legacy export for backwards compatibility
export const SelectionModal = StockCombobox;

export default StockCombobox;
